import { useEffect, useState, useCallback } from "react";
import { adminLogin, getToken, clearToken, fetchClosePortfolio, api } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-3 py-2 text-[var(--bone)] outline-none focus:border-[var(--gold)] mb-2";
const errMsg = (d, f) => (typeof d === "string" ? d : d?.message || d?.detail || f || "Something went wrong.");
const Unknown = () => <span className="italic text-[#e88f6a]">UNKNOWN</span>;

function Login({ onIn }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [err, setErr] = useState(null);
  const submit = async (e) => { e.preventDefault(); setErr(null);
    try { await adminLogin(email, pw); onIn(); } catch (e) { setErr(errMsg(e.response?.data?.detail, "Login failed.")); } };
  return (
    <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="p2p-login-form">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-xl mb-4">Founder Procure-to-Pay</h1>
        <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="p2p-login-email" aria-label="Email" />
        <input className={inputCls} type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="p2p-login-password" aria-label="Password" />
        <button className="jmh-cta mt-2" data-testid="p2p-login-submit">Enter</button>
        {err && <p className="mt-3 text-sm text-[#e88f6a]" data-testid="p2p-login-error">{err}</p>}
      </form>
    </div>
  );
}

const Panel = ({ title, items, testid, render }) => (
  <section className="mb-5" data-testid={testid}>
    <h3 className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-2">{title} · {items.length}</h3>
    {items.length === 0 ? <p className="text-sm text-[var(--muted)]" data-testid={`${testid}-empty`}>None.</p>
      : <ul className="border border-[rgba(180,139,63,0.18)] divide-y divide-[rgba(180,139,63,0.1)]">
          {items.map((it, i) => <li key={i} className="px-4 py-2 text-sm text-[var(--bone)]">{render(it)}</li>)}
        </ul>}
  </section>
);

function Console({ onSignOut }) {
  const [entities, setEntities] = useState([]); const [eid, setEid] = useState("");
  const [d, setD] = useState(null); const [err, setErr] = useState(null);
  useEffect(() => { fetchClosePortfolio().then((r) => {
    const list = (r.entities || []).map((e) => ({ id: e.entity_id, name: e.entity_name || e.legal_name || e.entity_id }));
    setEntities(list); if (!list.length) setErr("This workspace has no financial entities supported by verified source records. Establish those records before generating the calculations presented in this view."); if (list[0]) setEid(list[0].id);
  }).catch((e) => setErr(errMsg(e.response?.data?.detail))); }, []);
  const load = useCallback(() => { if (!eid) return; setD(null); setErr(null);
    api.get(`/fin/p2p/entities/${eid}/founder-view`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setD(r.data)).catch((e) => setErr(errMsg(e.response?.data?.detail, "Procurement view unavailable."))); }, [eid]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[var(--navy)] text-[var(--bone)]">
      <header className="border-b border-[rgba(180,139,63,0.2)] px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-lg">Founder Procure-to-Pay</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="bg-transparent border border-[rgba(180,139,63,0.3)] text-xs px-2 py-1" data-testid="p2p-entity-select" aria-label="Entity" value={eid} onChange={(e) => setEid(e.target.value)}>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.id.slice(0, 6)}</option>)}
          </select>
          <button onClick={onSignOut} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="p2p-signout">Sign out</button>
        </div>
      </header>
      <main className="px-4 md:px-8 py-6 max-w-6xl">
        {err ? <p className="text-sm text-[#e88f6a]" data-testid="p2p-error">{err}</p>
        : !d ? <p className="text-sm text-[var(--muted)]" data-testid="p2p-loading">Loading procurement…</p>
        : (
          <div data-testid="p2p-view">
            <p className="text-[11px] text-[var(--muted)] mb-1">{d.entity_name} · <span data-testid="p2p-consolidation">{d.consolidation_status}</span> · Stewardship: {d.stewardship_pointer}</p>
            <p className="text-[11px] text-[var(--muted)] mb-5">{d.authority_note}</p>

            <section data-testid="p2p-attention" className="mb-6">
              <h2 className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">What needs your attention · {d.attention.length}</h2>
              {d.attention.length === 0 ? <p className="text-sm text-[var(--muted)]">Nothing right now.</p>
                : <ul className="border border-[rgba(180,139,63,0.18)] divide-y divide-[rgba(180,139,63,0.1)]">
                    {d.attention.map((a, i) => <li key={i} className="px-4 py-2 text-sm text-[#e7a877] flex justify-between gap-4" data-testid={`p2p-attn-${i}`}>
                      <span>{a.kind.replace(/_/g, " ")}</span><span className="text-[var(--muted)]">{a.ref} · {a.reason_code}</span></li>)}
                  </ul>}
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <Panel title="Requests awaiting decision" items={d.requests_awaiting_decisions} testid="p2p-requests" render={(r) => `${r.request_id} · ${r.state} · $${r.requested_total}`} />
              <Panel title="Approved commitments" items={d.approved_commitments} testid="p2p-commitments" render={(c) => `${c.po_id} · ${c.state} · $${c.amount}`} />
              <Panel title="Outstanding purchase orders" items={d.outstanding_purchase_orders} testid="p2p-outstanding" render={(x) => x} />
              <Panel title="Partially received" items={d.partially_received} testid="p2p-partial" render={(x) => x} />
              <Panel title="Receipts awaiting bills" items={d.receipts_awaiting_bills} testid="p2p-receipts-awaiting" render={(x) => x} />
              <Panel title="Match exceptions" items={d.match_exceptions} testid="p2p-exceptions" render={(m) => `${m.match_id} · ${(m.reason_codes || []).join(", ")}`} />
              <Panel title="Unknown items" items={d.unknown_items} testid="p2p-unknowns" render={(m) => <>{m.match_id} · <Unknown /></>} />
              <Panel title="Disputed items" items={d.disputed_items} testid="p2p-disputed" render={(x) => x} />
              <Panel title="Over-receipts" items={d.over_receipts || []} testid="p2p-over-receipts" render={(o) => `${o.po_id} · ${o.state} · lines ${(o.over_receipt_lines || []).join(", ")} · ${o.reason_code}`} />
              <Panel title="Recurring commitments" items={d.recurring_commitments} testid="p2p-recurring" render={(r) => `${r.kind} · ${r.expected_amount == null ? "UNKNOWN" : "$" + r.expected_amount} · review ${r.review_status}`} />
            </div>

            {d.commitment_vs_actuals && (
              <section className="mt-8" data-testid="p2p-rollup">
                <h2 className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2">Commitment vs. actuals</h2>
                <p className="text-[11px] text-[var(--muted)] mb-3">{d.commitment_vs_actuals.note}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    ["Approved request", "approved_request"], ["Ordered commitment", "ordered_commitment"],
                    ["Received", "received"], ["Billed (AP)", "billed_ap"],
                    ["Posted AP liability", "posted_ap_liability"], ["Paid / reconciled cash", "paid_reconciled_cash"],
                  ].map(([label, key]) => {
                    const s = d.commitment_vs_actuals[key] || {};
                    return (
                      <div key={key} className="border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid={`p2p-rollup-${key}`}>
                        <div className="text-[10px] uppercase tracking-wide2 text-[var(--muted)] mb-1">{label}</div>
                        <div className="text-sm text-[var(--bone)]">{s.value == null ? <Unknown /> : `$${s.value}`}</div>
                        <div className="text-[10px] text-[var(--muted)] mt-1">{s.status}</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default function FounderProcureToPay() {
  const [authed, setAuthed] = useState(!!getToken());
  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  return <Console onSignOut={() => { clearToken(); setAuthed(false); }} />;
}
